/**
 * useDashboardStats Hook
 *
 * Fetches and transforms organization dashboard statistics
 * Supports both provider and admin endpoints via organizationId parameter
 */

import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { useGetAdminDashboardStatsQuery } from '../store/adminApi';
import { useGetDashboardStatsQuery } from '../store/api';
import type { DashboardStats, QualityDistributionData } from '../types';

interface UseDashboardStatsParams {
  /** Organization ID for admin mode (if provided, uses admin endpoint) */
  organizationId?: string;
  /** Period in days */
  days?: number;
  /** Explicit start date (ISO 8601 format) */
  dateFrom?: string;
  /** Explicit end date (ISO 8601 format) */
  dateTo?: string;
  /** Comma-separated provider IDs to filter by */
  userIds?: string;
}

interface UseDashboardStatsResult {
  /** Dashboard statistics */
  stats: DashboardStats | undefined;
  /** Quality distribution data */
  qualityDistribution: QualityDistributionData | undefined;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
  /** Computed metrics */
  computed: {
    /** Clean rate as percentage */
    cleanRate: number | null;
  };
}

export const useDashboardStats = (
  params?: UseDashboardStatsParams,
): UseDashboardStatsResult => {
  const filterParams = params
    ? {
        ...(params.days !== undefined && { days: params.days }),
        ...(params.dateFrom && { dateFrom: params.dateFrom }),
        ...(params.dateTo && { dateTo: params.dateTo }),
        ...(params.userIds && { userIds: params.userIds }),
      }
    : {};

  // Use admin endpoint if organizationId provided, otherwise use provider endpoint
  const adminQuery = useGetAdminDashboardStatsQuery(
    params?.organizationId
      ? {
          organizationId: params.organizationId,
          ...filterParams,
        }
      : skipToken,
  );

  const providerQuery = useGetDashboardStatsQuery(
    !params?.organizationId ? filterParams : skipToken,
  );

  // Use whichever query is active
  const { data, isLoading, error, refetch } = params?.organizationId
    ? adminQuery
    : providerQuery;

  const computed = useMemo(() => {
    if (!data) {
      return {
        cleanRate: null,
      };
    }

    const totalNotes = data.total_notes_audited || 0;
    const notesClean = data.notes_clean || 0;
    const cleanRate = totalNotes > 0 ? (notesClean / totalNotes) * 100 : null;

    return {
      cleanRate,
    };
  }, [data]);

  return {
    stats: data,
    qualityDistribution: data?.quality_distribution,
    isLoading,
    error: error ? 'Failed to load dashboard statistics' : null,
    refetch,
    computed,
  };
};

export default useDashboardStats;

